import React, { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Table, Tag, message } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  getPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  Promotion,
  PromotionFormData,
} from '../../Actions/promotionApi'
import withTitleMetaData from '../../hocs/withTitleMetaData'

const SERVICE_TYPE_OPTIONS = [
  { label: 'Aivatar', value: 'aivatar' },
  { label: 'Vgen', value: 'vgen' },
]

function Promotions() {
  const queryClient = useQueryClient()
  const [messageApi, contextHolder] = message.useMessage()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null)
  const [form] = Form.useForm()

  const { data: promotions = [], isLoading } = useQuery({
    queryKey: ['promotions'],
    queryFn: getPromotions,
  })

  const createMutation = useMutation({
    mutationFn: (body: PromotionFormData) => createPromotion(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] })
      messageApi.success('프로모션이 생성되었습니다.')
      closeModal()
    },
    onError: (e: Error) => messageApi.error(e.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: PromotionFormData }) => updatePromotion(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] })
      messageApi.success('프로모션이 수정되었습니다.')
      closeModal()
    },
    onError: (e: Error) => messageApi.error(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePromotion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] })
      messageApi.success('프로모션이 삭제되었습니다.')
    },
    onError: (e: Error) => messageApi.error(e.message),
  })

  const openCreateModal = useCallback(() => {
    setEditingPromotion(null)
    form.resetFields()
    setModalOpen(true)
  }, [form])

  const openEditModal = useCallback(
    (record: Promotion) => {
      setEditingPromotion(record)
      form.setFieldsValue({
        serviceType: record.serviceType,
        name: record.name,
        discountPercent: Math.round((1 - record.discountRate) * 100),
        period: [dayjs(record.startedAt), dayjs(record.expiredAt)],
      })
      setModalOpen(true)
    },
    [form],
  )

  const closeModal = useCallback(() => {
    setModalOpen(false)
    setEditingPromotion(null)
    form.resetFields()
  }, [form])

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()
      const body: PromotionFormData = {
        serviceType: values.serviceType,
        name: values.name || undefined,
        discountRate: 1 - values.discountPercent / 100,
        startedAt: values.period[0].format('YYYY-MM-DDTHH:mm:ss'),
        expiredAt: values.period[1].format('YYYY-MM-DDTHH:mm:ss'),
      }

      if (editingPromotion) {
        updateMutation.mutate({ id: editingPromotion.id, body })
      } else {
        createMutation.mutate(body)
      }
    } catch {
      // validation error
    }
  }, [form, editingPromotion, createMutation, updateMutation])

  const handleDelete = useCallback(
    (record: Promotion) => {
      Modal.confirm({
        title: '프로모션 삭제',
        content: `"${record.name || `ID: ${record.id}`}" 프로모션을 삭제하시겠습니까?`,
        okText: '삭제',
        okType: 'danger',
        cancelText: '취소',
        onOk: () => deleteMutation.mutate(record.id),
      })
    },
    [deleteMutation],
  )

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '서비스',
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 100,
      render: (text: string) => <Tag color={text === 'aivatar' ? 'blue' : 'purple'}>{text.toUpperCase()}</Tag>,
    },
    {
      title: '프로모션명',
      dataIndex: 'name',
      key: 'name',
      render: (text: string | null) => text || '-',
    },
    {
      title: '할인률',
      dataIndex: 'discountRate',
      key: 'discountRate',
      width: 100,
      render: (rate: number) => `${Math.round((1 - rate) * 100)}%`,
    },
    {
      title: '시작일',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 160,
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '종료일',
      dataIndex: 'expiredAt',
      key: 'expiredAt',
      width: 160,
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '상태',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      render: (active: boolean) => <Tag color={active ? 'green' : 'default'}>{active ? '활성' : '비활성'}</Tag>,
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: Promotion) => (
        <Row gutter={8}>
          <Col>
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Col>
          <Col>
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          </Col>
        </Row>
      ),
    },
  ]

  return (
    <div>
      {contextHolder}
      <Row justify="end" style={{ marginBottom: 16 }}>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={openCreateModal}>
            프로모션 추가
          </Button>
        </Col>
      </Row>

      <Table columns={columns} dataSource={promotions} rowKey="id" loading={isLoading} pagination={false} />

      <Modal
        title={editingPromotion ? '프로모션 수정' : '프로모션 생성'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        okText={editingPromotion ? '수정' : '생성'}
        cancelText="취소"
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="serviceType"
            label="서비스 타입"
            rules={[{ required: true, message: '서비스 타입을 선택해주세요.' }]}
          >
            <Select options={SERVICE_TYPE_OPTIONS} placeholder="서비스를 선택하세요" />
          </Form.Item>

          <Form.Item name="name" label="프로모션명">
            <Input placeholder="예: Vgen 런칭 프로모션" />
          </Form.Item>

          <Form.Item
            name="discountPercent"
            label="할인률 (%)"
            rules={[{ required: true, message: '할인률을 입력해주세요.' }]}
          >
            <InputNumber min={0} max={100} addonAfter="%" placeholder="50" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="period"
            label="프로모션 기간"
            rules={[{ required: true, message: '프로모션 기간을 선택해주세요.' }]}
          >
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default withTitleMetaData(Promotions)
